'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useState, useCallback, useRef, Suspense } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { Calendar, Sparkles, ChevronLeft, ChevronRight, Play, Newspaper, ExternalLink } from 'lucide-react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations, useLocale } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';
import { localeDateTag } from '@/lib/i18n-utils';
import { useSearchParams } from 'next/navigation';
import { Link } from '@/i18n/navigation';

interface MediaAppearance {
  url: string;
  title: string;
  localImage: string | null;
  type: 'article' | 'video' | 'website' | null;
  date: string | null;
  siteName: string | null;
  author: string | null;
}

const MEDIA_ARTICLES: MediaAppearance[] = [
  { url: 'https://cpp-luxury.com/in-conversation-with-lilla-racz-owner-of-castle-hotel-daniel-transylvania-exclusive-interview/', title: 'In Conversation with Lilla Racz, Owner of Castle Hotel Daniel, Transylvania – Exclusive Interview', localImage: '/design-images/uploads/1791291938185-top7mr-p_26.jpg', type: 'article', date: '2026-09-01', siteName: 'CPP Luxury', author: null },
  { url: 'https://prinlumecucopiii.ro/castelul-daniel-talisoara/', title: 'Castelul Daniel din Tălișoara - O destinație pentru întreaga familie', localImage: '/design-images/uploads/1789721674418-6pkrk8-vladimir-zeki-665-of-790-.jpg', type: 'article', date: null, siteName: 'Prin Lume cu Copiii', author: null },
  { url: 'https://agerpres.ro/2026/02/13/reportaj-covasna-facebook-ul-de-acum-400-de-ani-descoperit-la-castelul-daniel-din-talisoara-galerie---1527830', title: 'REPORTAJ Covasna: Facebook-ul de acum 400 de ani, descoperit la Castelul Daniel din Tălișoara', localImage: '/media-appearances/01.jpg', type: 'article', date: null, siteName: 'Agerpres', author: null },
  { url: 'https://www.profit.ro/profit-live/video-2-corporatisti-au-cumparat-castel-lilla-racz-co-fondatoare-castel-hotel-daniel-foarte-important-ne-concentram-oaspetii-straini-urmeze-faza-stabilitate-profit-live-tv-22239705', title: '2 corporatiști au cumpărat un castel. Lilla Racz, co-fondatoare Castel Hotel Daniel', localImage: '/media-appearances/02.jpg', type: 'article', date: '2025-11-17', siteName: 'Profit.ro', author: null },
  { url: 'https://www.businessmagazin.ro/analize/turism/povestea-unui-cuplu-de-corporatisti-ce-a-cumparat-un-castel-uitat-de-22935737', title: 'Povestea unui cuplu de corporatiști ce a cumpărat un castel uitat de lume', localImage: '/media-appearances/03.jpg', type: 'article', date: null, siteName: 'BusinessMagazin', author: null },
  { url: 'https://inspiratielafix.com/experienta-act-for-good-magie-intre-ani-la-castle-hotel-daniel/', title: 'Experienta Act for Good Magie: intre ani la Castle Hotel Daniel', localImage: '/media-appearances/04.jpg', type: 'article', date: '2026-01-03', siteName: 'Inspiratie la Fix', author: null },
  { url: 'https://www.imperatortravel.ro/2025/06/hotel-castel-daniel-pentru-o-experienta-cu-adevarat-aristocratica.html', title: 'Hotel Castel Daniel \u2013 pentru o experiență cu adevărat aristocratică', localImage: '/media-appearances/05.jpg', type: 'article', date: '2025-06-18', siteName: 'ImperatorTravel', author: null },
  { url: 'https://www.anapedia-travel.ro/castle-hotel-daniel-o-fila-de-poveste-scrisa-intr-un-castel-destinatii-inedite-in-romania-ep-36/', title: 'Castle Hotel Daniel \u2013 o filă de poveste scrisă într-un castel', localImage: '/media-appearances/06.jpg', type: 'article', date: '2025-05-19', siteName: 'AnaPedia Travel', author: null },
  { url: 'https://www.bronzaniada.ro/castelul-daniel-talisoara-castel-hotel-romania/', title: 'Castelul Daniel din Talisoara \u2013 Unul dintre puținele castele hotel din România', localImage: '/media-appearances/10.jpg', type: 'article', date: '2021-10-09', siteName: 'Bronzaniada', author: null },
  { url: 'https://stirileprotv.ro/romania-te-iubesc/tinutul-celor-100-de-castele-si-conace-e-sloganul-sub-care-proprietarii-din-covasna-invita-turistii-sa-le-descopere-povestea.html', title: 'Ținutul celor 100 de castele și conace', localImage: '/media-appearances/11.jpg', type: 'article', date: null, siteName: 'Știrile ProTV', author: null },
  { url: 'https://zilesinopti.ro/2021/03/22/haihui-cu-swit-rocs-viata-la-castelul-daniel/', title: 'HAIHUI cu Swit Rocs \u2013 Viața la castel(ul Daniel)', localImage: '/media-appearances/12.jpg', type: 'article', date: '2021-03-22', siteName: 'Zile și Nopți', author: null },
  { url: 'https://life.ro/daniel-castle-sau-cum-doi-tineri-corporatisti-reusesc-sa-rescrie-o-poveste-veche-de-400-de-ani-a-unei-familii-nobiliare-din-inima-transilvaniei-am-cumparat-din-pura-inconstienta-insa-cred-c/', title: 'Daniel Castle sau cum doi tineri corporatiști reușesc să rescrie o poveste veche de 400 de ani', localImage: '/media-appearances/14.jpg', type: 'article', date: '2020-09-06', siteName: 'LIFE.ro', author: null },
  { url: 'https://www.wildventure.ro/castel-hotel-daniel-talisoara-contact/', title: 'Castel Hotel Daniel, Covasna: Tarife imbatabile și disponibilități', localImage: '/media-appearances/15.jpg', type: 'article', date: '2019-04-26', siteName: 'Wildventure', author: null },
  { url: 'https://calatoriaperfecta.ro/pe-urmele-nobililor-la-castel-daniel/', title: 'Pe urmele nobililor la Castel Daniel', localImage: '/media-appearances/16.jpg', type: 'article', date: '2018-08-05', siteName: 'Calatoria Perfecta', author: null },
  { url: 'https://www.urbantravelblog.com/review/castle-hotel-daniel/', title: 'Castle Hotel Daniel - A Royal Transylvanian Experience', localImage: '/media-appearances/44.jpg', type: 'article', date: '2017-11-01', siteName: 'Urban Travel Blog', author: null },
  { url: 'https://zigzagprinromania.com/blog/castel-daniel-talisoara-o-experienta-unica-judetul-covasna/', title: 'Hotel Castel Daniel Tălișoara \u2013 O experiență unică în județul Covasna', localImage: '/media-appearances/19.jpg', type: 'article', date: '2018-02-09', siteName: 'Zig Zag prin Romania', author: null },
  { url: 'https://www.imperatortravel.ro/2019/04/weekend-nobiliar-la-castel-daniel-talisoara-judetul-covasna.html', title: 'Weekend nobiliar \u2013 la Castel Daniel Tălișoara, județul Covasna', localImage: '/media-appearances/20.jpg', type: 'article', date: '2019-04-18', siteName: 'ImperatorTravel', author: null },
  { url: 'https://www.zf.ro/business-construct/familia-racz-care-a-restaurat-acum-patru-ani-un-castel-din-transilvania-investeste-400-000-de-euro-in-extindere-17360960', title: 'Familia Racz investește 400.000 de euro în extinderea castelului din Transilvania', localImage: '/media-appearances/21.jpg', type: 'article', date: null, siteName: 'ZF.ro', author: null },
  { url: 'https://stirileprotv.ro/stiri/travel/fierarul-din-covasna-care-este-vizitat-de-mii-de-turisti-din-toata-europa-specialitatea-casei-la-nagy-gyorgy.html', title: 'Fierarul din Covasna care este vizitat de mii de turiști din toată Europa', localImage: '/media-appearances/23.jpg', type: 'article', date: null, siteName: 'Știrile ProTV', author: null },
  { url: 'https://aventurescu.ro/cat-costa-si-unde-te-poti-caza-la-un-castel-in-romania/', title: 'Cât costă și unde te poți caza la un castel în România', localImage: '/media-appearances/24.jpg', type: 'article', date: '2016-09-25', siteName: 'Aventurescu.ro', author: null },
  { url: 'https://www.vulpescu.eu/2016/09/23/cu-desaga-prin-tinutul-conacelor/', title: 'Cu desaga prin ținutul conacelor', localImage: null, type: null, date: null, siteName: 'Vulpescu', author: null },
  { url: 'https://www.bursa.ro/castelul-daniel-renovat-cu-peste-o-jumatate-de-milion-de-euro-47863037', title: 'Castelul Daniel, renovat cu peste o jumătate de milion de euro', localImage: '/media-appearances/26.jpg', type: 'website', date: null, siteName: 'Bursa.ro', author: null },
  { url: 'https://www.unanhaihui.ro/un-weekend-tinutul-celor-3-scaune-la-castel-daniel/', title: 'Un week-end in ținutul celor 3 scaune, la Castel Daniel', localImage: '/media-appearances/27.jpg', type: 'article', date: '2016-06-04', siteName: 'Un An HaiHui', author: null },
  { url: 'https://da.zf.ro/dupa-afaceri/travel/taramul-sutelor-de-conace-sau-cum-arata-regiunea-de-care-s-a-indragostit-si-printul-charles-si-care-a-ajuns-pe-primul-loc-in-topul-recomandarilor-lonely-planet-15042310', title: 'Tărâmul sutelor de conace \u2013 regiunea de care s-a îndrăgostit și prințul Charles', localImage: '/media-appearances/28.jpg', type: 'article', date: null, siteName: 'ZF.ro', author: null },
  { url: 'https://jurnaldehoinar.com/2015/12/28/castel-daniel-din-talisoara-o-istorie-veche-de-350-de-ani-reinterpretata-intr-o-atmosfera-noua-si-nobila-o-poveste-cu-final-fericit-si-cu-iz-princiar-care-inspira-si-re%C8%8Bnvie-simturile/', title: 'Castel Daniel din Tălișoara \u2013 o istorie veche de 350 de ani', localImage: '/media-appearances/29.jpg', type: 'article', date: '2015-12-28', siteName: 'Jurnal de Hoinar', author: null },
  { url: 'https://hotnews.ro/fotogalerie-cum-au-readus-la-viata-un-castel-de-400-de-ani-din-covasna-doi-tineri-corporatisti-din-bucuresti-si-cum-face-printul-charles-reclama-zonei-495658', title: 'Cum au readus la viață un castel de 400 de ani din Covasna. Doi tineri corporatiști din București și cum face prințul Charles reclamă zonei', localImage: '/design-images/uploads/1788345013623-sb9ezo-image-2015-11-21-20610543-41-castel-daniel-6.webp', type: 'article', date: '2015-11-21', siteName: 'HotNews', author: null },
  { url: 'https://tetovainyenc.ro/2015/11/15/reneszansz-vacsora-a-daniel-kastelyban/', title: 'Reneszánsz vacsora a Daniel kastélyban', localImage: '/media-appearances/30.jpg', type: 'article', date: '2015-11-15', siteName: 'Tétova ínyenc', author: null },
  { url: 'https://cavaleria.ro/tinutul-conacelor-covasna-un-loc-pe-care-trebuie-sa-il-vizitezi/', title: 'Ținutul Conacelor \u2013 Un loc pe care trebuie să îl vizitezi', localImage: '/media-appearances/31.jpg', type: 'article', date: '2015-10-13', siteName: 'Cavaleria.ro', author: null },
  { url: 'https://korinams.ro/2015/10/04/hotel-castel-daniel-fotografii-si-impresii/', title: 'Hotel Castel Daniel \u2013 fotografii și impresii', localImage: '/media-appearances/32.jpg', type: 'article', date: '2015-10-03', siteName: 'KorinaMS', author: null },
  { url: 'https://www.amusebouche.ro/daniel-castle-weekend-in-tinutul-conacelor/', title: 'Daniel Castle: weekend în Ținutul Conacelor', localImage: '/media-appearances/34.jpg', type: 'article', date: '2015-09-15', siteName: 'Amuse Bouche', author: null },
  { url: 'https://adevarul.ro/stiri-locale/sibiu/povestea-corporatistilor-romani-care-si-au-1650832.html', title: 'Povestea corporatiștilor români care și-au cumpărat castel cu 100.000 de euro', localImage: '/media-appearances/35.jpg', type: 'article', date: null, siteName: 'Adevărul', author: null },
  { url: 'https://razvanpascu.ro/2015/08/29/un-weekend-la-castel-daniel-castle-din-covasna-impresii-si-fotografii/', title: 'Un weekend la castel. Daniel Castle din Covasna \u2013 impresii și fotografii', localImage: '/media-appearances/36.jpg', type: 'article', date: '2015-08-29', siteName: 'Razvan Pascu', author: null },
  { url: 'https://www.digi24.ro/magazin/timp-liber/divertisment/romania-mea-doi-tineri-au-reabilitat-un-castel-construit-pe-la-1600-in-judetul-covasna-398629', title: 'ROMÂNIA MEA. Doi tineri au reabilitat un castel construit pe la 1600, în județul Covasna', localImage: '/media-appearances/37.jpg', type: 'article', date: '2015-06-02', siteName: 'Digi24', author: null },
  { url: 'https://bazavan.ro/2015/05/cum-am-descoperit-romania-la-castle-hotel-daniel/', title: 'Cum am descoperit România. La Castle Hotel Daniel', localImage: '/media-appearances/38.jpg', type: 'article', date: '2015-05-12', siteName: 'Blogul Cristinei Bazavan', author: null },
  { url: 'https://www.imperatortravel.ro/2015/04/un-weekend-aristocratic-la-castelul-daniel-din-talisoara-covasna.html', title: 'Un weekend aristocratic la Castelul Daniel din Tălișoara, Covasna', localImage: '/media-appearances/39.jpg', type: 'article', date: '2015-04-07', siteName: 'ImperatorTravel', author: null },
  { url: 'https://www.digi24.ro/magazin/timp-liber/divertisment/cazare-la-conac-turismul-nobiliar-din-ce-in-ce-mai-cautat-350073', title: 'Cazare la conac. Turismul nobiliar, din ce în ce mai căutat', localImage: '/media-appearances/40.jpg', type: 'article', date: '2015-01-21', siteName: 'Digi24', author: null },
  { url: 'https://maszol.ro/eletmod/Erdely-legjei-Kovaszna-megye-az-olaszteleki-Daniel-kastely', title: 'Erdély legjei – Kovászna megye: az olaszteleki Daniel-kastély', localImage: '/media-appearances/41.jpg', type: 'article', date: '2023-06-04', siteName: 'Maszol', author: null },
  { url: 'https://matusinka.ro/daniel-kastely/', title: 'Dániel-kastély', localImage: '/media-appearances/42.jpg', type: 'article', date: '2024-03-27', siteName: 'Matusinka.ro', author: null },
  { url: 'https://www.3szek.ro/load/cikk/174182/alkotasra-termett-olasztelek-muvesztelep-a-daniel-kastelyban', title: 'Alkotásra termett Olasztelek – Művésztelep a Daniel-kastélyban', localImage: '/media-appearances/43.jpg', type: 'article', date: '2025-06-12', siteName: '3szek.ro', author: null },
];

const MEDIA_VIDEOS: MediaAppearance[] = [
  { url: 'https://www.youtube.com/watch?v=J6GkxwBvyQo', title: 'Wellness in 400 Jahre altem Schloss | Siebenbürgen königlich genießen | Discover Transylvania', localImage: '/media-appearances/44.jpg', type: 'video', date: null, siteName: null, author: 'FILMKULTUR' },
  { url: 'https://www.youtube.com/watch?v=aGYbP2ZBoJY', title: 'COVASNA, ȚINUTUL CONACELOR ȘI CASTELELOR', localImage: '/media-appearances/07.jpg', type: 'video', date: null, siteName: null, author: 'TVR Tîrgu-Mureș' },
  { url: 'https://www.youtube.com/watch?v=MlGfDrqj2pE', title: 'Akkor lehet előre haladni, ha autentikus vagy \u2013 Rácz Lilla és Rácz Attila', localImage: '/media-appearances/08.jpg', type: 'video', date: null, siteName: null, author: 'PERTU' },
  { url: 'https://www.youtube.com/watch?v=raTmpoaItvg', title: 'Castel Daniel', localImage: '/media-appearances/09.jpg', type: 'video', date: null, siteName: null, author: 'catalin55' },
  { url: 'https://www.youtube.com/watch?v=JDDbE7G306g', title: 'Au lasat orasul si s-au mutat intr-un castel vechi de sute de ani!', localImage: '/media-appearances/13.jpg', type: 'video', date: null, siteName: null, author: 'Kanal D Romania' },
  { url: 'https://www.youtube.com/watch?v=_a8t6dtonkk&t=184s', title: 'Tiltott kastélyok 8. \u2013 Olaszteleki Danielek', localImage: '/media-appearances/17.jpg', type: 'video', date: null, siteName: null, author: 'Gábor Margittai' },
  { url: 'https://www.youtube.com/watch?v=3Eu9uIQIxn8', title: 'Castel Daniel', localImage: '/media-appearances/18.jpg', type: 'video', date: null, siteName: null, author: 'calatoria perfecta' },
  { url: 'https://www.youtube.com/watch?v=NaPZ310hTMA', title: 'ROventura \u2013 Varghis Canyon, a show of nature and a rustic adventure!', localImage: '/media-appearances/22.jpg', type: 'video', date: null, siteName: null, author: 'Kanal D Romania' },
  { url: 'https://www.youtube.com/watch?v=aX_EZeeKabU', title: 'Egy udvarház új élete', localImage: '/media-appearances/33.jpg', type: 'video', date: null, siteName: null, author: 'Tekla Magyari' },
];

function formatMediaDate(dateStr: string, localeTag: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(localeTag, { month: 'short', year: 'numeric' });
}

function MediaCarousel({ items }: { items: MediaAppearance[] }) {
  const t = useTranslations('design.aboutUs.media');
  const locale = useLocale();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <div className="flex items-center gap-4">
      {/* Previous button */}
      <button
        onClick={scrollPrev}
        className="flex-shrink-0 w-10 h-10 rounded-full bg-[#801515] flex items-center justify-center text-white transition-transform duration-300 hover:scale-110"
        aria-label={t('previous')}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      {/* Carousel */}
      <div className="overflow-hidden py-4 px-2 -mx-2 min-w-0 flex-1" ref={emblaRef}>
        <div className="flex -ml-5">
          {items.map((item, i) => (
            <div key={i} className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_25%] min-w-0 pl-5">
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-full"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  {item.localImage ? (
                    <Image
                      src={item.localImage}
                      alt={item.title}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      style={{ objectPosition: item.url.includes('prinlumecucopiii') ? 'center top' : undefined }}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f0ece6] to-[#e8e0d6] flex items-center justify-center">
                      <Newspaper className="w-12 h-12 text-[#801515]/25" />
                    </div>
                  )}
                  {item.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                        <Play className="w-7 h-7 text-white fill-white ml-0.5" />
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <ExternalLink className="absolute top-3 right-3 w-4 h-4 text-white drop-shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-4">
                  <h4 className="font-poppins text-sm font-semibold text-gray-900 leading-snug line-clamp-4 mb-2 group-hover:text-[#801515] transition-colors duration-300">
                    {item.title}
                  </h4>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span className="truncate mr-2">{item.siteName || item.author || 'Media'}</span>
                    {item.date && <span className="flex-shrink-0">{formatMediaDate(item.date, localeDateTag(locale))}</span>}
                  </div>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Next button */}
      <button
        onClick={scrollNext}
        className="flex-shrink-0 w-10 h-10 rounded-full bg-[#801515] flex items-center justify-center text-white transition-transform duration-300 hover:scale-110"
        aria-label={t('next')}
      >
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
}

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'aboutUs')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

type MapLocation = {
  id: string;
  x: number;
  y: number;
  color: string;
  label: string;
};

const MAP_LOCATIONS: MapLocation[] = [
  { id: 'garden',      x: 9,  y: 33, color: '#4A6741', label: 'Vegetable Garden' },
  { id: 'event-barn',  x: 26, y: 34, color: '#4A6741', label: 'Event Barn' },
  { id: 'exhibition',  x: 32, y: 18, color: '#4A6741', label: 'Exhibition Hall' },
  { id: 'grill',       x: 43, y: 21, color: '#4A6741', label: 'Grill Terrace' },
  { id: 'bethlen',     x: 68, y: 19, color: '#801515', label: 'Bethlen Castle' },
  { id: 'restaurant',  x: 61, y: 24, color: '#9B6B2F', label: 'Restaurant' },
  { id: 'spa',         x: 69, y: 28, color: '#478B8B', label: 'Cellarium Spa' },
  { id: 'parking',     x: 19, y: 44, color: '#444444', label: 'Parking Lot' },
  { id: 'daniel',      x: 64, y: 61, color: '#801515', label: 'Daniel Castle' },
  { id: 'reception',   x: 40, y: 46, color: '#9B6B2F', label: 'Reception' },
  { id: 'crafts',      x: 26, y: 54, color: '#801515', label: 'Crafts House' },
  { id: 'mini-farm',   x: 8,  y: 13, color: '#4A6741', label: 'Mini Farm' },
  { id: 'entrance',    x: 82, y: 75, color: '#444444', label: 'Entrance' },
];

const LEGEND_ENTRIES: { locId: string; tKey: string; color: string }[] = [
  { locId: 'daniel',     tKey: 'danielCastle',  color: '#801515' },
  { locId: 'bethlen',    tKey: 'bethlenCastle', color: '#801515' },
  { locId: 'reception',  tKey: 'reception',     color: '#9B6B2F' },
  { locId: 'exhibition', tKey: 'exhibition',    color: '#4A6741' },
  { locId: 'event-barn', tKey: 'eventBarn',     color: '#4A6741' },
  { locId: 'parking',    tKey: 'parking',       color: '#444444' },
];

function AboutUsV1Inner() {
  const t = useTranslations('design.aboutUs');
  const locale = useLocale();
  const searchParams = useSearchParams();
  const mapEdit = searchParams.get('mapEdit') === '1';
  const [activeTimeline, setActiveTimeline] = useState<number | null>(null);
  const [activeTeam, setActiveTeam] = useState<number | null>(null);
  const [activeLocation, setActiveLocation] = useState<string | null>(null);
  const [mapPositions, setMapPositions] = useState<Record<string, { x: number; y: number }>>(
    () => Object.fromEntries(MAP_LOCATIONS.map(l => [l.id, { x: l.x, y: l.y }]))
  );
  const dragRef = useRef<{ id: string; startX: number; startY: number; origX: number; origY: number } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const getLocalizedLabel = (locId: string): string => {
    if (locale === 'hu' || locale === 'ro') {
      const labelMap: Record<string, string> = {
        'garden': t('estateToday.mapLabels.garden'),
        'event-barn': t('estateToday.mapLabels.eventBarn'),
        'exhibition': t('estateToday.mapLabels.exhibition'),
        'grill': t('estateToday.mapLabels.grill'),
        'bethlen': t('estateToday.mapLabels.bethlen'),
        'restaurant': t('estateToday.mapLabels.restaurant'),
        'spa': t('estateToday.mapLabels.spa'),
        'parking': t('estateToday.mapLabels.parking'),
        'daniel': t('estateToday.mapLabels.daniel'),
        'reception': t('estateToday.mapLabels.reception'),
        'crafts': t('estateToday.mapLabels.crafts'),
        'mini-farm': t('estateToday.mapLabels.miniFarm'),
        'entrance': t('estateToday.mapLabels.entrance'),
      };
      return labelMap[locId] || MAP_LOCATIONS.find(l => l.id === locId)?.label || locId;
    }
    return MAP_LOCATIONS.find(l => l.id === locId)?.label || locId;
  };
  const activeLoc = (() => {
    const loc = MAP_LOCATIONS.find(l => l.id === activeLocation);
    if (!loc) return null;
    const pos = mapPositions[loc.id];
    return pos ? { ...loc, x: pos.x, y: pos.y } : loc;
  })();

  // --- Drag handlers for temporary marker repositioning ---
  const handlePointerDown = useCallback((e: React.PointerEvent, locId: string) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const pos = mapPositions[locId];
    dragRef.current = { id: locId, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  }, [mapPositions]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || !mapContainerRef.current) return;
    if (!mapEdit) return;
    const rect = mapContainerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - drag.startX) / rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / rect.height) * 100;
    const newX = Math.round(Math.min(100, Math.max(0, drag.origX + dx)));
    const newY = Math.round(Math.min(100, Math.max(0, drag.origY + dy)));
    setMapPositions(prev => ({ ...prev, [drag.id]: { x: newX, y: newY } }));
  }, [mapEdit]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = Math.abs(e.clientX - drag.startX);
    const dy = Math.abs(e.clientY - drag.startY);
    const wasTap = dx < 5 && dy < 5;
    dragRef.current = null;

    if (wasTap || !mapEdit) {
      // Tap: toggle label (same dot closes, different dot switches).
      // When mapEdit is off, drag is locked, so always treat the gesture as a tap.
      setActiveLocation(prev => prev === drag.id ? null : drag.id);
    } else {
      // Drag (admin only): dump all current positions for easy copy-paste back
      // into this file's MAP_LOCATIONS source. This is a raw, uncapped console
      // dump on purpose — the structured logger would JSON-escape and size-cap
      // the block, making it useless to paste. Not operational logging.
      const snapshot = MAP_LOCATIONS.map(l => {
        const pos = mapPositions[l.id];
        return `  { id: '${l.id}',${' '.repeat(Math.max(1, 12 - l.id.length))}x: ${pos.x},${pos.x < 10 ? '  ' : ' '}y: ${pos.y},${pos.y < 10 ? '  ' : ' '}color: '${l.color}', label: '${l.label}' },`;
      });
      // eslint-disable-next-line no-console -- verbatim dev copy-paste helper
      console.log(`// ── MAP_LOCATIONS snapshot ──\nconst MAP_LOCATIONS: MapLocation[] = [\n${snapshot.join('\n')}\n];`);
    }
  }, [mapPositions, mapEdit]);

  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes drawLine {
          from { height: 0; }
          to { height: 100%; }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
        .timeline-line {
          animation: drawLine 2s ease-out forwards;
        }
      `}</style>

      <NavPreview />

      {/* Hero Section - Full Image */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Full-width background image */}
        <div className="absolute inset-0">
          <Image
            src="/design-images/uploads/1771340831108-whatsapp-image-2026-02-17-at-10.24.26.jpg"
            alt={t('imageAlts.aerial')}
            fill
            className="object-cover image-breathe-slow"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
        </div>

        <div className="relative h-full flex flex-col items-center justify-end pb-[18vh] md:pb-[16vh] px-6 text-center">
          <div className="observe-me slide-up w-full max-w-3xl">
            {/* Main Title */}
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>

            {/* Decorative Line with Subtitle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
                {t('hero.subtitle')}
              </p>
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>

            {/* Third Subtitle */}
            <p className="text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light mb-12 [text-shadow:rgba(0,0,0,1)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Historical Highlights Intro */}
      <section className="py-24 lg:py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center observe-me slide-up">
          <p className="text-sm uppercase tracking-[0.3em] text-[#801515] font-semibold mb-4">
            {t('historicalIntro.label')}
          </p>
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-8 leading-[1.1]">
            {t('historicalIntro.heading')}
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto mb-10" />
          <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed max-w-3xl mx-auto font-light italic">
            {t('historicalIntro.description')}
          </p>
        </div>
      </section>

      {/* Historical Narrative - Full Width Text Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto space-y-8 observe-me slide-up">
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.rich('historicalNarrative.para1', { strong: (chunks) => <strong>{chunks}</strong> })}
          </p>

          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.rich('historicalNarrative.para2', { strong: (chunks) => <strong>{chunks}</strong> })}
          </p>

          <div className="py-8 text-center">
            <p className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">
              {t('historicalNarrative.highlight1')}
            </p>
            <p className="text-3xl md:text-4xl font-poppins font-bold text-gray-800 mt-2">
              {t('historicalNarrative.highlight2')}
            </p>
          </div>

          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.rich('historicalNarrative.para3', { strong: (chunks) => <strong>{chunks}</strong>, em: (chunks) => <em>{chunks}</em> })}
          </p>

          <div className="py-6">
            <p className="text-3xl md:text-4xl font-poppins font-bold text-gray-600 text-center">
              {t('historicalNarrative.timeHighlight')}
            </p>
          </div>

          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.rich('historicalNarrative.para4', { strong: (chunks) => <strong>{chunks}</strong> })}
          </p>

          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t('historicalNarrative.para5')}
          </p>

          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.rich('historicalNarrative.para6', { strong: (chunks) => <strong>{chunks}</strong> })}
          </p>

          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t.rich('historicalNarrative.para7', {
              link: (chunks) => <Link href="/activities#exhibition" className="underline decoration-[#801515]/40 hover:decoration-[#801515] hover:text-[#801515] transition-colors">{chunks}</Link>
            })}
          </p>
        </div>
      </section>

      {/* Personal Story - Lilla and Attila */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
        <div className="max-w-4xl mx-auto px-6 text-center observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
            <span className="text-[#801515]">{t('personalStory.heading')}</span>
            <br />
            <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
              {t('personalStory.subheading')}
            </span>
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
            {t('personalStory.intro')}
          </p>
          <div className="mt-12 relative rounded-3xl overflow-hidden shadow-2xl max-w-3xl mx-auto">
            <div className="relative h-[400px] md:h-[500px] lg:h-[550px]">
              <Image
                src="/design-images/uploads/1771584393834-p_15.jpg"
                alt={t('imageAlts.foundersTogether')}
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white text-left">
                <p className="text-lg md:text-xl font-poppins font-semibold">{t('personalStory.photoCaption')}</p>
                <p className="text-sm md:text-base text-white/80 mt-1">{t('personalStory.photoCaptionSub')}</p>
              </div>
            </div>
          </div>

          {/* Their Story - Full Text */}
          <div className="max-w-3xl mx-auto mt-20 text-left space-y-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {t.rich('personalStory.para1', {
                strong: (chunks) => <strong className="text-gray-900">{chunks}</strong>,
                lilla: (chunks) => <a href="https://www.linkedin.com/in/lilla-r%C3%A1cz-a0b1b473/" target="_blank" rel="noopener noreferrer" className="underline decoration-[#801515]/40 hover:decoration-[#801515] hover:text-[#801515] transition-colors">{chunks}</a>,
                attila: (chunks) => <a href="https://www.linkedin.com/in/raczattila/" target="_blank" rel="noopener noreferrer" className="underline decoration-[#801515]/40 hover:decoration-[#801515] hover:text-[#801515] transition-colors">{chunks}</a>
              })}
            </p>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {t.rich('personalStory.para2', { strong: (chunks) => <strong>{chunks}</strong> })}
            </p>

            <div className="py-6 text-center">
              <p className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">
                {t('personalStory.highlight1')}
              </p>
            </div>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {t.rich('personalStory.para3', { strong: (chunks) => <strong>{chunks}</strong>, em: (chunks) => <em>{chunks}</em> })}
            </p>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {t.rich('personalStory.para4', { strong: (chunks) => <strong>{chunks}</strong>, highlight: (chunks) => <span className="text-[#801515] font-semibold">{chunks}</span> })}
            </p>

            <div className="py-6 text-center">
              <p className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">
                {t('personalStory.highlight2')}
              </p>
            </div>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {t.rich('personalStory.para5', { strong: (chunks) => <strong>{chunks}</strong> })}
            </p>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              {t.rich('personalStory.para6', { strong: (chunks) => <strong>{chunks}</strong> })}
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Section - CRITICAL DESIGN ELEMENT */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-[#faf8f6] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Timeline Header */}
          <div className="text-center mb-20 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('timeline.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('timeline.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('timeline.description')}
            </p>
          </div>

          {/* Timeline Visualization */}
          <div className="relative">
            {/* Central vertical line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#801515] via-[#801515] to-transparent -translate-x-1/2 hidden lg:block timeline-line" />

            {/* Timeline Items */}
            <div className="space-y-24 lg:space-y-32">
              {/* 2008 - Acquisition with Portrait Image */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                {/* Left: Image (on large screens) */}
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[400px] lg:h-[500px]"
                    onMouseEnter={() => setActiveTimeline(0)}
                    onMouseLeave={() => setActiveTimeline(null)}
                  >
                    <Image
                      src="/about-us-images/resized/1768904706632-DSCN3968.JPG"
                      alt={t('imageAlts.renovation')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTimeline === 0 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-sm font-semibold mb-1">{t('timeline.y2008caption')}</p>
                      <p className="text-lg">{t('timeline.y2008captionSub')}</p>
                    </div>
                  </div>
                </div>

                {/* Right: Date and Description */}
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#801515] flex items-center justify-center shadow-lg">
                      <Calendar className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-5xl md:text-6xl font-poppins font-bold text-[#801515]">2008</p>
                      <div className="h-1 w-12 bg-[#801515] mt-2" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-poppins font-semibold text-gray-900 mb-4">
                    {t('timeline.y2008title')}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('timeline.y2008desc')}
                  </p>
                </div>

                {/* Central dot for timeline */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#801515] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* Renovation Documentation Image - Between 2008 and 2014 */}
              <div className="relative observe-me scale-in">
                <div className="max-w-4xl mx-auto">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px] lg:h-[600px]">
                    <Image
                      src="/design-images/uploads/1775033236969-dscn9577.jpg.jpg"
                      alt={t('imageAlts.restoration')}
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-8 left-8 right-8 text-white">
                      <p className="text-2xl md:text-3xl font-poppins font-bold mb-2">{t('timeline.challengeTitle')}</p>
                      <p className="text-lg md:text-xl">
                        {t('timeline.challengeDesc')}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Central dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-gray-400 rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 2014 - Castle Opens */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                {/* Left: Date and Description */}
                <div className="lg:pr-8 lg:text-right">
                  <div className="flex lg:flex-row-reverse items-center gap-4 mb-6 lg:justify-end">
                    <div className="w-16 h-16 rounded-full bg-[#801515] flex items-center justify-center shadow-lg">
                      <Calendar className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-5xl md:text-6xl font-poppins font-bold text-[#801515]">2014</p>
                      <div className="h-1 w-12 bg-[#801515] mt-2 lg:ml-auto" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-poppins font-semibold text-gray-900 mb-4">
                    {t('timeline.y2014title')}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('timeline.y2014desc')}
                  </p>
                </div>

                {/* Right: Grand Opening Image */}
                <div className="relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[300px] lg:h-[400px]"
                    onMouseEnter={() => setActiveTimeline(1)}
                    onMouseLeave={() => setActiveTimeline(null)}
                  >
                    <Image
                      src="/design-images/uploads/1771584602372-img_3411-pano.jpg"
                      alt={t('imageAlts.grandOpening')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTimeline === 1 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-sm font-semibold mb-1">{t('timeline.y2014caption')}</p>
                      <p className="text-lg">{t('timeline.y2014captionSub')}</p>
                    </div>
                  </div>
                </div>

                {/* Central dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#801515] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 2018 - Bethlen Castle Added */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                {/* Left: Spa Image */}
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#478B8B]/30 hover:scale-105 h-[300px] lg:h-[400px]"
                    onMouseEnter={() => setActiveTimeline(2)}
                    onMouseLeave={() => setActiveTimeline(null)}
                  >
                    <Image
                      src="/design-images/uploads/1771584844898-img_0145.jpg"
                      alt={t('imageAlts.spa')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTimeline === 2 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-sm font-semibold mb-1">{t('timeline.y2018caption')}</p>
                      <p className="text-lg">{t('timeline.y2018captionSub')}</p>
                    </div>
                  </div>
                </div>

                {/* Right: Date and Description */}
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#478B8B] flex items-center justify-center shadow-lg">
                      <Calendar className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-5xl md:text-6xl font-poppins font-bold text-[#478B8B]">2018</p>
                      <div className="h-1 w-12 bg-[#478B8B] mt-2" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-poppins font-semibold text-gray-900 mb-4">
                    {t('timeline.y2018title')}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('timeline.y2018desc')}
                  </p>
                </div>

                {/* Central dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#478B8B] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 2022 - Gardens and Exhibition */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                {/* Left: Date and Description */}
                <div className="lg:pr-8 lg:text-right">
                  <div className="flex lg:flex-row-reverse items-center gap-4 mb-6 lg:justify-end">
                    <div className="w-16 h-16 rounded-full bg-[#4A6741] flex items-center justify-center shadow-lg">
                      <Calendar className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-5xl md:text-6xl font-poppins font-bold text-[#4A6741]">2022</p>
                      <div className="h-1 w-12 bg-[#4A6741] mt-2 lg:ml-auto" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-poppins font-semibold text-gray-900 mb-4">
                    {t('timeline.y2022title')}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('timeline.y2022desc')}
                  </p>
                </div>

                {/* Right: Exhibition Image */}
                <div className="relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#4A6741]/30 hover:scale-105 h-[300px] lg:h-[400px]"
                    onMouseEnter={() => setActiveTimeline(3)}
                    onMouseLeave={() => setActiveTimeline(null)}
                  >
                    <Image
                      src="/design-images/uploads/1771585057115-ray06484.jpg"
                      alt={t('imageAlts.exhibition1680')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTimeline === 3 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-sm font-semibold mb-1">{t('timeline.y2022caption')}</p>
                      <p className="text-lg">{t('timeline.y2022captionSub')}</p>
                    </div>
                  </div>
                </div>

                {/* Central dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#4A6741] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 2026 - Coming Soon */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                {/* Left: Crafts House Image */}
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[300px] lg:h-[400px]"
                    onMouseEnter={() => setActiveTimeline(4)}
                    onMouseLeave={() => setActiveTimeline(null)}
                  >
                    <Image
                      src="/design-images/uploads/1771585147651-sc_04.jpg"
                      alt={t('imageAlts.craftsHouse')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTimeline === 4 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-sm font-semibold mb-1">{t('timeline.y2026caption')}</p>
                      <p className="text-lg">{t('timeline.y2026captionSub')}</p>
                    </div>
                  </div>
                </div>

                {/* Right: Date and Description */}
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#801515] to-[#601010] flex items-center justify-center shadow-lg">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-5xl md:text-6xl font-poppins font-bold text-[#801515]">2026</p>
                      <div className="h-1 w-12 bg-[#801515] mt-2" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-poppins font-semibold text-gray-900 mb-4">
                    {t('timeline.y2026title')}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('timeline.y2026desc')}
                  </p>
                </div>

                {/* Central dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-gradient-to-br from-[#801515] to-[#601010] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10 animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-2 h-2 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 4s ease-in-out infinite' }} />
        <div className="absolute bottom-32 left-16 w-3 h-3 bg-[#478B8B] rounded-full opacity-35" style={{ animation: 'pulse 5s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      {/* The Estate Today — Interactive Map */}
      <section className="relative bg-gradient-to-br from-[#f0f4f0] via-white to-[#faf8f6] overflow-hidden">
        <div className="grid lg:grid-cols-5 lg:min-h-screen items-center">
          {/* Text + Legend - 2 columns */}
          <div className="lg:col-span-2 design-content-pad lg:p-8 xl:p-12 order-2 lg:order-1">
            <div className="max-w-lg mx-auto lg:mx-0 observe-me slide-left">
              <h2 className="font-poppins text-[clamp(2rem,3vw,3.5rem)] font-bold mb-3 leading-[1.1]">
                <span className="text-[#4A6741]">{t('estateToday.heading')}</span>
                <br />
                <span className="text-gray-800 text-lg sm:text-2xl md:text-[clamp(1.5rem,2.5vw,2.75rem)] font-normal mt-1.5 block">
                  {t('estateToday.subheading')}
                </span>
              </h2>
              <div className="w-16 h-1 bg-[#4A6741] mb-4" />
              <p className="text-lg text-gray-600 mb-6">
                {t('estateToday.intro')}
              </p>
              <ul className="space-y-0.5 text-base text-gray-700">
                {LEGEND_ENTRIES.map((entry) => {
                  const isActive = activeLocation === entry.locId;
                  return (
                    <li
                      key={entry.locId}
                      onMouseEnter={() => setActiveLocation(entry.locId)}
                      onMouseLeave={() => setActiveLocation(null)}
                      className={`flex items-start gap-3 px-2 py-1.5 rounded-xl cursor-pointer transition-all duration-300 ${
                        isActive
                          ? 'bg-white shadow-lg translate-x-1 ring-1 ring-black/5'
                          : 'hover:bg-white/60'
                      }`}
                    >
                      <span
                        className={`mt-2 flex-shrink-0 rounded-full transition-all duration-300 ${
                          isActive ? 'w-3 h-3' : 'w-2 h-2'
                        }`}
                        style={{
                          backgroundColor: entry.color,
                          boxShadow: isActive ? `0 0 0 5px ${entry.color}33` : undefined,
                        }}
                      />
                      <span>
                        {entry.tKey === 'parking'
                          ? t('estateToday.parking')
                          : t.rich(`estateToday.${entry.tKey}`, { strong: (chunks) => <strong>{chunks}</strong> })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Interactive Aerial Map - 3 columns */}
          <div className="lg:col-span-3 p-0 lg:p-8 xl:p-12 order-1 lg:order-2">
            <div className="relative w-full max-w-[960px] mx-auto observe-me slide-right">
              <div
                ref={mapContainerRef}
                className="relative w-full"
                style={{ aspectRatio: '1920 / 1278' }}
                onMouseLeave={() => setActiveLocation(null)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
              >
                {/* Image frame with clipping */}
                <div className="absolute inset-0 rounded-none lg:rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-[#e8ebe5]">
                  <Image
                    src="/about-us-images/resized/1769756723295-blurred_hd.jpg"
                    alt={t('imageAlts.estateMap')}
                    fill
                    className="object-contain select-none pointer-events-none"
                    sizes="(min-width: 1024px) 55vw, 100vw"
                    priority
                  />

                  {/* Soft vignette */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.28) 100%)',
                    }}
                  />

                  {/* Colored radial tint following the active pin */}
                  {activeLoc && (
                    <div
                      className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                      style={{
                        background: `radial-gradient(circle at ${activeLoc.x}% ${activeLoc.y}%, ${activeLoc.color}40 0%, transparent 28%)`,
                      }}
                    />
                  )}

                </div>

                {/* Pins (rendered outside clip so tooltips can overflow) */}
                {MAP_LOCATIONS.map((loc) => {
                  const isActive = activeLocation === loc.id;
                  const pos = mapPositions[loc.id];
                  return (
                    <div
                      key={loc.id}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none touch-none ${mapEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${isActive ? 'z-20' : 'z-10'}`}
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                      onMouseEnter={() => setActiveLocation(loc.id)}
                      onMouseLeave={() => { if (!dragRef.current) setActiveLocation(null); }}
                      onPointerDown={(e) => handlePointerDown(e, loc.id)}
                      role="button"
                      aria-label={getLocalizedLabel(loc.id)}
                    >
                      {/* Outer glow */}
                      <span
                        aria-hidden
                        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full blur-xl transition-opacity duration-500 ${
                          isActive ? 'opacity-70' : 'opacity-0'
                        }`}
                        style={{ backgroundColor: loc.color }}
                      />

                      {/* Ripple rings (only when active) */}
                      {isActive && (
                        <>
                          <span
                            aria-hidden
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full animate-ping opacity-60"
                            style={{ backgroundColor: loc.color }}
                          />
                          <span
                            aria-hidden
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full animate-ping opacity-30"
                            style={{ backgroundColor: loc.color, animationDelay: '0.4s' }}
                          />
                        </>
                      )}

                      {/* Ambient pulse (always) */}
                      <span
                        aria-hidden
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full opacity-25 animate-pulse"
                        style={{ backgroundColor: loc.color }}
                      />

                      {/* Main pin dot */}
                      <span
                        className={`relative block rounded-full ring-2 ring-white shadow-lg transition-all duration-300 ${
                          isActive ? 'w-5 h-5 scale-125' : 'w-3.5 h-3.5'
                        }`}
                        style={{ backgroundColor: loc.color }}
                      />

                      {/* Label — hidden on mobile (tap to show), always visible on desktop */}
                      <span
                        className={`absolute z-10 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold whitespace-nowrap shadow-xl backdrop-blur-sm transition-all duration-300 ${
                          isActive ? 'opacity-100' : 'opacity-0 sm:opacity-100 pointer-events-none sm:pointer-events-auto'
                        } ${
                          loc.id === 'spa'
                            ? `left-1/2 -translate-x-1/2 top-full mt-2 ${isActive ? 'scale-110 translate-y-1 shadow-2xl' : ''}`
                            : loc.id === 'restaurant'
                              ? `right-full mr-2 top-1/2 -translate-y-1/2 ${isActive ? 'scale-110 -translate-x-1 shadow-2xl' : ''}`
                              : `left-1/2 -translate-x-1/2 bottom-full mb-2 ${isActive ? 'scale-110 -translate-y-1 shadow-2xl' : ''}`
                        }`}
                        style={{ backgroundColor: `${loc.color}${isActive ? 'ee' : 'cc'}`, color: 'white', border: '1px solid rgba(255,255,255,0.45)' }}
                      >
                        {getLocalizedLabel(loc.id)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Hint */}
              <p className="text-center text-xs uppercase tracking-[0.25em] text-gray-500 mt-5">
                {t.has('estateToday.mapHint') ? t('estateToday.mapHint') : 'Hover each point to explore the estate'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guiding Principles */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#faf8f6] to-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-20 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('principles.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('principles.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('principles.description')}
            </p>
          </div>

          {/* Mission & Vision */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="observe-me slide-left bg-white rounded-3xl p-10 shadow-xl border-t-4 border-[#801515]">
              <p className="text-sm uppercase tracking-[0.25em] text-[#801515] font-semibold mb-3">{t('principles.missionLabel')}</p>
              <h3 className="font-poppins text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('principles.missionTitle')}</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('principles.missionText')}
              </p>
            </div>
            <div className="observe-me slide-right bg-white rounded-3xl p-10 shadow-xl border-t-4 border-[#801515]">
              <p className="text-sm uppercase tracking-[0.25em] text-[#801515] font-semibold mb-3">{t('principles.visionLabel')}</p>
              <h3 className="font-poppins text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('principles.visionTitle')}</h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('principles.visionText')}
              </p>
            </div>
          </div>

          {/* Core Values */}
          <div className="text-center mb-12 observe-me slide-up">
            <p className="text-sm uppercase tracking-[0.25em] text-[#801515] font-semibold">{t('principles.coreValuesLabel')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Heritage */}
            <div className="observe-me slide-up bg-gradient-to-br from-[#801515] to-[#601010] rounded-3xl p-8 text-white shadow-xl" style={{ animationDelay: '0s' }}>
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-2xl font-poppins font-bold">H</span>
              </div>
              <h4 className="font-poppins text-xl font-bold mb-3">{t('principles.heritageTitle')}</h4>
              <p className="text-white/85 leading-relaxed">
                {t('principles.heritageText')}
              </p>
            </div>

            {/* Authenticity */}
            <div className="observe-me slide-up bg-gradient-to-br from-[#801515] to-[#601010] rounded-3xl p-8 text-white shadow-xl" style={{ animationDelay: '0.15s' }}>
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-2xl font-poppins font-bold">A</span>
              </div>
              <h4 className="font-poppins text-xl font-bold mb-3">{t('principles.authenticityTitle')}</h4>
              <p className="text-white/85 leading-relaxed">
                {t('principles.authenticityText')}
              </p>
            </div>

            {/* Care */}
            <div className="observe-me slide-up bg-gradient-to-br from-[#801515] to-[#601010] rounded-3xl p-8 text-white shadow-xl" style={{ animationDelay: '0.3s' }}>
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-2xl font-poppins font-bold">C</span>
              </div>
              <h4 className="font-poppins text-xl font-bold mb-3">{t('principles.careTitle')}</h4>
              <p className="text-white/85 leading-relaxed">
                {t('principles.careText')}
              </p>
            </div>

            {/* Slow Living */}
            <div className="observe-me slide-up bg-gradient-to-br from-[#801515] to-[#601010] rounded-3xl p-8 text-white shadow-xl" style={{ animationDelay: '0.45s' }}>
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-2xl font-poppins font-bold">S</span>
              </div>
              <h4 className="font-poppins text-xl font-bold mb-3">{t('principles.slowLivingTitle')}</h4>
              <p className="text-white/85 leading-relaxed">
                {t('principles.slowLivingText')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Swan Team */}
      <section className="py-24 lg:py-32 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('swanTeam.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('swanTeam.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('swanTeam.description')}
            </p>
          </div>

          {/* Team Photo */}
          <div className="max-w-4xl mx-auto observe-me slide-up">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[400px] md:h-[500px] lg:h-[560px]">
              <Image
                src="/design-images/uploads/1771585848721-p_27.jpg"
                alt={t('imageAlts.team')}
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="text-lg font-poppins font-semibold">{t('swanTeam.photoCaption')}</p>
                <p className="text-sm text-white/80 mt-1">{t('swanTeam.photoCaptionSub')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Departments Timeline */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-[#faf8f6] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-20 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('teams.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('teams.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('teams.description')}
            </p>
          </div>

          {/* Team Timeline */}
          <div className="relative">
            {/* Central vertical line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#801515] via-[#478B8B] to-[#4A6741] -translate-x-1/2 hidden lg:block timeline-line" />

            <div className="space-y-24 lg:space-y-32">
              {/* 1 - Reception Team */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(0)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1772107671310-ray04856.jpg"
                      alt={t('imageAlts.receptionTeam')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 0 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.receptionTitle')}</p>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#801515] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      1
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">{t('teams.receptionTitle')}</h3>
                      <div className="h-1 w-12 bg-[#801515] mt-2" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.receptionDesc')}
                  </p>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#801515] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 2 - Corporate Relationships Manager */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="lg:pr-8 lg:text-right">
                  <div className="flex lg:flex-row-reverse items-center gap-4 mb-6 lg:justify-end">
                    <div className="w-14 h-14 rounded-full bg-[#801515] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      2
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">{t('teams.corporateTitle')}</h3>
                      <div className="h-1 w-12 bg-[#801515] mt-2 lg:ml-auto" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.corporateDesc')}
                  </p>
                </div>
                <div className="relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(1)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1772524014318-42.jpg"
                      alt={t('imageAlts.corporateManager')}
                      fill
                      className="object-cover"
                      style={{ objectPosition: 'center 5%' }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 1 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.corporateCaption')}</p>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#801515] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 3 - Restaurant & Service Team */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(2)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1772107920271-p_02.jpg"
                      alt={t('imageAlts.serviceTeam')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 2 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.restaurantCaption')}</p>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#801515] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      3
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">{t('teams.restaurantTitle')}</h3>
                      <div className="h-1 w-12 bg-[#801515] mt-2" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.restaurantDesc')}
                  </p>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#801515] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 4 - Kitchen Team */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="lg:pr-8 lg:text-right">
                  <div className="flex lg:flex-row-reverse items-center gap-4 mb-6 lg:justify-end">
                    <div className="w-14 h-14 rounded-full bg-[#801515] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      4
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#801515]">{t('teams.kitchenTitle')}</h3>
                      <div className="h-1 w-12 bg-[#801515] mt-2 lg:ml-auto" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.kitchenDesc')}
                  </p>
                </div>
                <div className="relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#801515]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(3)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1772184868197-0007ds2_5061.jpg"
                      alt={t('imageAlts.kitchenTeam')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 3 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.kitchenTitle')}</p>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#801515] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 5 - Spa Team */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#478B8B]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(4)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1776759114031-ds1_3484-edit.jpg"
                      alt={t('imageAlts.spaTeam')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 4 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.spaTitle')}</p>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#478B8B] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      5
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#478B8B]">{t('teams.spaTitle')}</h3>
                      <div className="h-1 w-12 bg-[#478B8B] mt-2" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.spaDesc')}
                  </p>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#478B8B] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 6 - Housekeeping Team */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="lg:pr-8 lg:text-right">
                  <div className="flex lg:flex-row-reverse items-center gap-4 mb-6 lg:justify-end">
                    <div className="w-14 h-14 rounded-full bg-[#4A6741] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      6
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#4A6741]">{t('teams.housekeepingTitle')}</h3>
                      <div className="h-1 w-12 bg-[#4A6741] mt-2 lg:ml-auto" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.housekeepingDesc')}
                  </p>
                </div>
                <div className="relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#4A6741]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(5)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1772108095455-p_03.jpg"
                      alt={t('imageAlts.housekeepingTeam')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 5 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.housekeepingTitle')}</p>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#4A6741] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>

              {/* 7 - Maintenance Team */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center relative observe-me fade-in">
                <div className="order-2 lg:order-1 relative">
                  <div
                    className="relative rounded-3xl overflow-hidden shadow-2xl cursor-pointer transition-all duration-500 hover:shadow-[#4A6741]/30 hover:scale-105 h-[350px] lg:h-[420px]"
                    onMouseEnter={() => setActiveTeam(6)}
                    onMouseLeave={() => setActiveTeam(null)}
                  >
                    <Image
                      src="/design-images/uploads/1772108189373-p_06.jpg"
                      alt={t('imageAlts.maintenanceTeam')}
                      fill
                      className="object-cover"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${activeTeam === 6 ? 'opacity-100' : 'opacity-80'}`} />
                    <div className="absolute bottom-6 left-6 right-6 text-white">
                      <p className="text-lg font-poppins font-semibold">{t('teams.maintenanceTitle')}</p>
                    </div>
                  </div>
                </div>
                <div className="order-1 lg:order-2 lg:pl-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#4A6741] flex items-center justify-center shadow-lg text-white font-poppins font-bold text-xl">
                      7
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-4xl font-poppins font-bold text-[#4A6741]">{t('teams.maintenanceTitle')}</h3>
                      <div className="h-1 w-12 bg-[#4A6741] mt-2" />
                    </div>
                  </div>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {t('teams.maintenanceDesc')}
                  </p>
                </div>
                <div className="hidden lg:block absolute left-1/2 top-1/2 w-6 h-6 bg-[#4A6741] rounded-full -translate-x-1/2 -translate-y-1/2 ring-4 ring-white shadow-lg z-10" />
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-2 h-2 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 4s ease-in-out infinite' }} />
        <div className="absolute bottom-32 right-16 w-3 h-3 bg-[#478B8B] rounded-full opacity-35" style={{ animation: 'pulse 5s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      {/* Living Castle Foundation */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
        <div className="max-w-4xl mx-auto px-6 text-center observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
            <span className="text-[#801515]">{t('foundation.heading')}</span>
            <br />
            <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
              {t('foundation.subheading')}
            </span>
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
          <div className="max-w-5xl mx-auto space-y-10 text-left mt-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <p className="md:w-1/2 text-xl md:text-2xl text-gray-700 leading-relaxed">
                {t.rich('foundation.para1', { strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
              <div className="md:w-1/2 flex-shrink-0 flex items-center justify-center">
                <div className="relative w-[260px] h-[260px] md:w-[300px] md:h-[300px]">
                  <Image
                    src="/design-images/uploads/1771941455938-livingcastle.jpg"
                    alt={t('imageAlts.foundationLogo')}
                    fill
                    className="object-contain"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <p className="md:w-1/2 text-xl md:text-2xl text-gray-700 leading-relaxed">
                {t.rich('foundation.para2', { strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
              <div className="md:w-1/2 flex-shrink-0">
                <div className="relative rounded-2xl overflow-hidden shadow-xl h-[280px] md:h-[320px]">
                  <Image
                    src="/design-images/uploads/1772185390464-dsc01782.jpg"
                    alt={t('imageAlts.artCamp')}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-sm font-poppins font-semibold">{t('foundation.artCampCaption')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <p className="md:w-1/2 text-xl md:text-2xl text-gray-700 leading-relaxed">
                {t.rich('foundation.para3', { strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
              <div className="md:w-1/2 flex-shrink-0">
                <div className="relative rounded-2xl overflow-hidden shadow-xl h-[280px] md:h-[320px]">
                  <Image
                    src="/design-images/uploads/1771943511957-ray06481.jpg"
                    alt={t('imageAlts.exhibitionHall')}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-sm font-poppins font-semibold">{t('foundation.exhibitionCaption')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Paragraph 4 - Renaissance Music Festival with event image */}
            <div className="flex flex-col md:flex-row items-center gap-8">
              <p className="md:w-1/2 text-xl md:text-2xl text-gray-700 leading-relaxed">
                {t.rich('foundation.para4', { strong: (chunks) => <strong>{chunks}</strong> })}
              </p>
              <div className="md:w-1/2 flex-shrink-0">
                <div className="relative rounded-2xl overflow-hidden shadow-xl h-[280px] md:h-[320px]">
                  <Image
                    src="/design-images/uploads/1771597047738-dsc03631.jpg"
                    alt={t('imageAlts.musicFestival')}
                    fill
                    className="object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-sm font-poppins font-semibold">{t('foundation.festivalCaption')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Appearances Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#faf8f6] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins font-bold mb-6 leading-[1.1]">
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal block mb-3">
                {t('media.headingPrefix')}
              </span>
              <span className="text-[#801515] text-[clamp(2.25rem,3.5vw,4rem)] block">
                {t('media.heading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-6" />
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('media.description')}
            </p>
          </div>

          {/* Articles */}
          <div className="mb-16 observe-me fade-in">
            <div className="flex items-center gap-3 mb-8">
              <Newspaper className="w-5 h-5 text-[#801515]" />
              <h3 className="font-poppins text-2xl md:text-3xl font-bold text-gray-900">{t('media.articles')}</h3>
              <span className="text-sm text-gray-400 font-medium">({MEDIA_ARTICLES.length})</span>
            </div>
            <MediaCarousel items={MEDIA_ARTICLES} />
          </div>

          {/* Videos */}
          <div className="observe-me fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center gap-3 mb-8">
              <Play className="w-5 h-5 text-[#801515]" />
              <h3 className="font-poppins text-2xl md:text-3xl font-bold text-gray-900">{t('media.videos')}</h3>
              <span className="text-sm text-gray-400 font-medium">({MEDIA_VIDEOS.length})</span>
            </div>
            <MediaCarousel items={MEDIA_VIDEOS} />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faq.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <FAQAccordion />
        </div>
      </section>

      <Footer />
    </main>
  );
}

export default function AboutUsV1() {
  return (
    <Suspense>
      <AboutUsV1Inner />
    </Suspense>
  );
}
